// In EmailDetail.js - update the attachments section to show horizontally
import React from "react";
import { Button, IconButton, Chip, Box } from "@material-ui/core";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReplyIcon from "@mui/icons-material/Reply";
import ReplyAllIcon from "@mui/icons-material/ReplyAll";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import InboxIcon from "@mui/icons-material/Inbox";
import moment from "moment";

const EmailDetail = ({ email, onBack, onReply, onMenuClick }) => {
  if (!email) {
    return (
      <div className='email-detail-empty'>
        <div className='empty-state'>
          <InboxIcon style={{ fontSize: 64, color: "#dadce0" }} />
          <h3>No email selected</h3>
          <p>Select an email to read</p>
        </div>
      </div>
    );
  }

  // Format file size to human readable format
  const formatFileSize = bytes => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Get appropriate icon for file type
  const getFileIcon = (contentType, filename) => {
    if (contentType.includes("image")) {
      return <ImageIcon fontSize='small' />;
    } else if (contentType.includes("pdf")) {
      return <PictureAsPdfIcon fontSize='small' />;
    } else if (
      contentType.includes("word") ||
      contentType.includes("document")
    ) {
      return <DescriptionIcon fontSize='small' />;
    } else if (filename) {
      const extension = filename.split(".").pop().toLowerCase();
      if (["jpg", "jpeg", "png", "gif", "bmp"].includes(extension)) {
        return <ImageIcon fontSize='small' />;
      } else if (["pdf"].includes(extension)) {
        return <PictureAsPdfIcon fontSize='small' />;
      } else if (["doc", "docx", "txt", "rtf"].includes(extension)) {
        return <DescriptionIcon fontSize='small' />;
      }
    }
    return <AttachFileIcon fontSize='small' />;
  };

  // Handle attachment download
  const handleDownloadAttachment = attachment => {
    if (attachment.contentBytes) {
      // Create a blob from base64 content
      const byteCharacters = atob(attachment.contentBytes);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.contentType });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.name || "attachment";
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      // If no contentBytes, maybe the API provides a download URL
      console.warn("Attachment content not available in response");
      alert("Attachment download not available. Please try again later.");
    }
  };

  return (
    <div className='email-detail-panel'>
      <div className='email-detail-header'>
        <div className='email-detail-header-left'>
          <div className='header-back-action'>
            <h2>{email.subject}</h2>
          </div>
          <div className='email-labels'>
            {email.labels?.map((label, index) => (
              <span
                key={index}
                className='email-label'
                style={{ backgroundColor: "#e8eaed" }}>
                {label}
              </span>
            ))}
          </div>
        </div>
        <div className='email-detail-actions'>
          <IconButton
            onClick={() => onReply("reply")}
            title='Reply'
            size='small'>
            <ReplyIcon />
          </IconButton>
          <IconButton
            onClick={() => onReply("replyAll")}
            title='Reply All'
            size='small'>
            <ReplyAllIcon />
          </IconButton>
          <IconButton onClick={onMenuClick} size='small'>
            <MoreVertIcon />
          </IconButton>
        </div>
      </div>

      <div className='email-detail-content'>
        <div className='email-detail-sender'>
          <div className='sender-avatar'>
            {email.fromName.charAt(0).toUpperCase()}
          </div>
          <div className='sender-info'>
            <div className='sender-name'>
              <strong>{email.fromName}</strong>
              <span className='sender-email'>{email.from}</span>
            </div>
            <div className='email-meta'>
              <span className='email-time'>
                {moment(email.timestamp).format("MMMM D, YYYY h:mm A")}
              </span>
              <span className='email-to'>to {email.to.join(", ")}</span>
            </div>
          </div>
        </div>

        <div className='email-detail-body'>
          <div dangerouslySetInnerHTML={{ __html: email.body }} />

          {email.attachments && email.attachments.length > 0 && (
            <div className='email-attachments'>
              <div className='attachments-header'>
                <AttachFileIcon fontSize='small' />
                <span>Attachments ({email.attachments.length})</span>
              </div>

              <div className='attachments-grid'>
                {email.attachments.map((attachment, index) => (
                  <div key={index} className='attachment-card'>
                    <div className='attachment-card-header'>
                      <div className='attachment-icon'>
                        {getFileIcon(attachment.contentType, attachment.name)}
                      </div>
                      <div className='attachment-card-info'>
                        <div
                          className='attachment-name'
                          title={attachment.name}>
                          {attachment.name}
                        </div>
                        <div className='attachment-size'>
                          {formatFileSize(attachment.size)}
                        </div>
                      </div>
                    </div>

                    {attachment.contentType.includes("image") &&
                      attachment.contentBytes && (
                        <div className='attachment-preview'>
                          <img
                            src={`data:${attachment.contentType};base64,${attachment.contentBytes}`}
                            alt={attachment.name}
                            className='preview-image'
                          />
                        </div>
                      )}

                    <div className='attachment-card-actions'>
                      <Button
                        variant='outlined'
                        size='small'
                        fullWidth
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadAttachment(attachment)}
                        style={{ textTransform: "none" }}>
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className='email-reply-actions'>
          <Button
            variant='outlined'
            startIcon={<ReplyIcon />}
            onClick={() => onReply("reply")}
            style={{ textTransform: "none" }}>
            Reply
          </Button>
          <Button
            variant='outlined'
            startIcon={<ReplyAllIcon />}
            onClick={() => onReply("replyAll")}
            style={{ textTransform: "none" }}>
            Reply All
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailDetail;
